import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createVideoSkill: Skill = {
    name: SKILL_NAMES.createVideo,
    description:
        'Create a standard video. The default video skill — load it whenever no more specific one fits the request.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Marketing video',
        blurb: 'A standard marketing video from a link, doc or script.',
        icon: 'movie',
        gradient: ['#5B6390', '#5E8CA6'],
    },
    instructions: `# Create Video

Lumen5 best practices for general videos. This skill helps make the video feel professionally made.

## Narration
- **The video gets an AI voiceover.** Create an AI voiceover by default (load the
  \`${SKILL_NAMES.aiVoiceoverVideoBasics}\` skill for that build), unless the user explicitly said
  otherwise: they gave you a talking-head video of someone speaking on camera (load
  the \`${SKILL_NAMES.talkingHeadVideoBasics}\` skill for that build), they gave you their own
  recorded voiceover audio (load the \`${SKILL_NAMES.recordedVoiceoverVideoBasics}\` skill for that
  build), or they specifically asked for a text-only/silent video (load the
  \`${SKILL_NAMES.textVideoBasics}\` skill for that build).
  If it's unclear *which* of those they meant, ask.
- Pick a voice that fits the target audience info you have (accent, language) and tone of the video.
- **Never use em dashes (—) in the script or in on-screen text.** Write the two
  halves as separate sentences, or join them with a comma or a conjunction.
- Well-placed pauses give the narration room to breathe — a beat after the opening
  hook, before a key point, ahead of the closing line. Sparingly, not between every
  sentence.

## Setup defaults
- **Length**: use the user's stated target; otherwise pick a sensible default for
  the topic and tell them what you assumed.
- **Aspect ratio**: as stated, or what their platform implies (social → vertical,
  web → landscape). Only ask if it's truly ambiguous and actually matters.
- **Template**: pick for subject and tone (cozy, playful, corporate…) — look at
  what's available and search if none seem like an obvious choice.

## Storyboard opinions
- Build the **narrative spine** first — the ordered ideas the video must hit —
  before exact wording, so one changed scene doesn't force a full rewrite.
- **Open on a title scene** with an effective, specific title that says exactly what
  the video is about — not a vague headline, and never a cold drop into the first
  fact. **Close on a real outro** — a wrap-up, takeaway, or call to action — never
  an abrupt stop after the last point.
- For each scene, decide whether media is central to the point (a specific product,
  place, thing) or illustrative background. When central, plan that scene's script
  and media together against the media you scouted — don't write wording first and
  hope matching media shows up.
- **List and count fidelity.** If a scene is a running list — even 3 items — mark it
  for a list-format design and capture the complete set of items. When narration
  states a count, the visuals show every item and the numbers match: never announce
  four and show three. If a design can't hold all the items, split the list across
  scenes or pick a design that can.
- If narration references step **numbers** ("step 1", "the second reason"), the
  design must actually display that number — spoken-only numbers are a defect.
- Write final wording budgeted to the target duration — scenes and word count fit
  the duration, not the other way around — and word it with the build in mind:
  natural clause breaks where overlay splits will land.

## Build opinions
- **Many short scenes, not one long one.** A narration video is a run of short
  visual moments. If the user asks for the deliberate audiogram/waveform style,
  load the \`${SKILL_NAMES.createPodcastWaveformVideo}\` skill instead.
- **Pacing**: assume a full sentence needs two or more overlays/subscenes at
  speaking pace; cut only at natural break points and let a scene run slightly long
  rather than snapping a phrase. When one sentence spans several overlays, keep its
  design and media constant across them so it reads as one continuous moment.
- **Vary the designs.** Never the same design on two consecutive scenes; actively
  rotate through the catalog rather than leaning on one or two looks. Repeating is
  only fine when content genuinely calls for a matching treatment (items of one
  list) — and even then, not endlessly.
- **Match the design to the content**: does each text asset comfortably fit what
  you're putting in it (shape-bounded assets — circles, badges, pills — hold one or
  two words, no more)? Does the asset count match what the scene has to say (a
  design with more text assets than you'll fill produces leftover placeholder)?
  A design missing a needed asset is the wrong design; it can't be retrofitted.
- List-like content gets a listicle/list design whose asset count fits the item
  count. Title/intro designs only at the open; outro/disclaimer only at the end.
- **Avoid back-to-back text-only scenes** — after a text scene, the next one should
  carry media. Use multi-column / two-up / icon-row layouts only when the content
  genuinely maps to them.
- **Caption-style designs** (ANIMATED_CAPTIONS category) are a good deliberate
  choice for a scene with no strong media — not a default, and not never.
- **Text**: on-screen text must stand on its own — trim for brevity, never for
  meaning (no cryptic fragments like "The shift"). Aim for a font size around 60 or
  above; small type is a signal there's too much text in the scene, and the fix is
  splitting, not shrinking. In a block of 6+ words, highlight the key word or
  phrase.
- **Media**: the chosen clip should obviously fit the point — a viewer half-paying-
  attention should get why it's on screen. Vaguely-on-topic is not enough; search
  again rather than settle. Reframe within the asset so the subject is well-composed
  and nothing important sits under text (\`${SKILL_NAMES.cropMedia}\` skill). Load \`${SKILL_NAMES.findUserMedia}\` before going through their material and \`${SKILL_NAMES.findStockMedia}\` before your first stock search (\`${SKILL_NAMES.findDamMedia}\` too when a DAM is connected — brand/company content likely lives there).
- **Stats worth seeing become charts**, not plain text — load the
  \`${SKILL_NAMES.chartsBasics}\` skill before adding one.
- **Captions**: leave the inherited setting alone unless the user asks.
- **Music**: add background music unless the user said otherwise. Pick
  a track that fits the tone and set it.
- Colors and styling come from the template or the user's brand kit — apply a kit
  when one exists or the user references their brand (\`${SKILL_NAMES.createBrandKit}\` skill);
  don't hand-pick color overrides unprompted.

## Finishing
Review and verify per the system prompt's arc. Load the \`${SKILL_NAMES.reviewVideo}\` skill BEFORE
starting the review pass — it has the procedure and the pass/fail bar.`,
};
